import { html, cx, render, useEffect, useState } from './h.js'
import { Icon } from './icons.js'
import { t } from './i18n.js'
import { AppContext, createStore, useStore } from './store.js'
import { syncBackWithHistory } from './back.js'
import { ToastHost, Lightbox, Avatar } from './components.js'
import { Onboarding } from './screens/onboarding.js'
import { ChatList } from './screens/chatlist.js'
import { Conversation } from './screens/conversation.js'
import { InfoPanel } from './screens/panel.js'
import { StoryViewer } from './screens/stories.js'
import { Settings, Contacts } from './screens/settings.js'
import { Dialogs } from './screens/dialogs.js'
import { IncomingCall, CallOverlay } from './screens/call.js'
import { CallManager, parseCollectLink } from '@pine/core'
import { messagePreview } from './format.js'

/**
 * Mount the whole app.
 *
 * The same call is used by the web build, the Electron renderer and the
 * Android WebView — the only thing that differs between them is the client
 * that gets handed in, which is exactly the seam the transport layer creates.
 */
export function mountApp (element, { client, platform = {} }) {
  let calls = null
  try {
    calls = new CallManager(client, { iceServers: platform.iceServers })
  } catch {
    calls = null // no WebRTC in this runtime; the UI hides call buttons
  }

  const store = createStore(client, { calls, platform })
  render(html`<${Root} store=${store} />`, element)
  return store
}

function Root ({ store }) {
  return html`
    <${AppContext.Provider} value=${store}>
      <${ToastHost}>
        <${Shell} />
      <//>
    <//>`
}

function Shell () {
  const store = useStore()
  const { client } = store
  const [ready, setReady] = useState(() => client.onboarded)

  useNotifications(store)
  useDeepLinks(store)
  useEffect(() => syncBackWithHistory(store), [store])

  if (!ready) {
    return html`<${Onboarding} client=${client} onDone=${() => setReady(true)} />`
  }

  const route = store.get('route')
  const lightbox = store.get('lightbox')
  const mobileView = store.get('mobileView')

  return html`
    <div class="shell" data-view=${mobileView}>
      <${Rail} />

      ${route === 'chats' && html`<${ChatList} />`}
      ${route === 'contacts' && html`<${Contacts} />`}
      ${route === 'settings' && html`<${Settings} />`}

      ${route === 'chats' && html`<${Conversation} />`}
      ${route === 'chats' && store.get('panel') && store.get('roomId') && html`<${InfoPanel} />`}

      <${Dialogs} />
      ${store.get('storyViewer') && html`<${StoryViewer} />`}
      <${IncomingCall} />
      <${CallOverlay} />

      ${lightbox && html`
        <${Lightbox} src=${lightbox.src} mime=${lightbox.mime} name=${lightbox.name}
          onClose=${() => store.closeLightbox()}
          onDownload=${() => {
            const a = document.createElement('a')
            a.href = lightbox.src
            a.download = lightbox.name || 'file'
            a.click()
          }} />`}
    </div>`
}

function Rail () {
  const store = useStore()
  const route = store.get('route')
  const unread = store.client.totalUnread

  const items = [
    { id: 'chats', icon: Icon.chat(22), label: t('nav.chats'), badge: unread },
    { id: 'contacts', icon: Icon.contacts(22), label: t('nav.contacts'), badge: store.client.requests.length },
    { id: 'settings', icon: Icon.settings(22), label: t('nav.settings'), badge: 0 }
  ]

  return html`
    <nav class="rail">
      <div class="brand" title="Pine">${Icon.pine(24)}</div>
      ${items.map((item) => html`
        <button key=${item.id} class=${cx('rail-btn', route === item.id && 'active')}
                title=${item.label} aria-label=${item.label}
                onClick=${() => store.set({ route: item.id, mobileView: 'list', settingsPage: 'home' })}>
          ${item.icon}
          ${item.badge > 0 && html`<span class="badge">${item.badge > 99 ? '99+' : item.badge}</span>`}
        </button>`)}
      <div class="grow spacer"></div>
      <${Avatar} name=${store.client.identity.profile.name} id=${store.client.identity.id} size="sm"
        src=${store.client.identity.profile.avatar || null}
        onClick=${() => store.set({ route: 'settings', mobileView: 'list', settingsPage: 'profile' })} />
    </nav>`
}

/* ------------------------------------------------------------------ *
 * Platform glue
 * ------------------------------------------------------------------ */

function useNotifications (store) {
  useEffect(() => {
    const { client, platform } = store

    const off = client.on('notification', async ({ roomId, roomName, message, unread }) => {
      if (!client.settings.notifications) return
      if (store.get('roomId') === roomId && document.visibilityState === 'visible') return

      const body = messagePreview(message)
      const title = roomName || 'Pine'

      if (platform.notify) {
        platform.notify({ title, body, roomId, unread })
        return
      }
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return

      const registration = typeof navigator !== 'undefined' && navigator.serviceWorker
        ? await navigator.serviceWorker.getRegistration()
        : null

      if (registration && registration.showNotification) {
        registration.showNotification(title, {
          body,
          tag: roomId,
          icon: '/icons/icon-192.png',
          badge: '/icons/badge.png',
          data: { roomId }
        })
      } else {
        const n = new Notification(title, { body, tag: roomId })
        n.onclick = () => {
          window.focus()
          store.openRoom(roomId)
        }
      }
    })

    return off
  }, [store])

  // Keep the app badge in step with the unread total where the API exists.
  useEffect(() => {
    const update = () => {
      const total = store.client.totalUnread
      if (store.platform.setBadge) store.platform.setBadge(total)
      else if (navigator.setAppBadge) {
        if (total > 0) navigator.setAppBadge(total).catch(() => {})
        else navigator.clearAppBadge().catch(() => {})
      }
      document.title = total > 0 ? `(${total}) Pine` : 'Pine'
    }
    update()
    return store.subscribe(update)
  }, [store])
}

/**
 * Invite links open the app rather than a web page: /i/<code> is picked up
 * here, and the same handler serves the custom pine:// scheme that the
 * desktop and Android builds register.
 */
function useDeepLinks (store) {
  useEffect(() => {
    const handle = (raw) => {
      if (!raw) return
      /*
       * A chat, from a notification or the call screen. Nothing used to
       * read these: tapping a message notification opened the app on
       * whatever it showed last, not on the chat. "push:<account>" is a
       * call the phone rang for on the push alone, before it knew the
       * room: the chat with that person.
       */
      const chat = String(raw).match(/^pine:\/\/room\/([^/?#]+)/i)
      if (chat) {
        let roomId = decodeURIComponent(chat[1])
        if (roomId.startsWith('push:')) {
          const peer = roomId.slice(5)
          const record = store.client.listRooms().find((r) => r.peer === peer && !r.deleted)
          roomId = record ? record.id : ''
        }
        if (roomId && store.client.getSummary(roomId)) store.openRoom(roomId).catch(() => {})
        return
      }
      const token = parseCollectLink(raw)
      if (token) return store.showDialog({ type: 'collect', token })
      const match = String(raw).match(/(?:pine:\/\/join\/|\/i\/|[?&]i=)([a-z0-9]+)/i)
      if (match) store.showDialog({ type: 'join', code: match[1] })
    }

    handle(location.pathname + location.search)
    if (location.pathname.startsWith('/i/') || location.pathname.startsWith('/c/')) history.replaceState({}, '', '/')

    const onHash = () => handle(location.hash)
    window.addEventListener('hashchange', onHash)

    const off = store.platform.onDeepLink ? store.platform.onDeepLink(handle) : null
    return () => {
      window.removeEventListener('hashchange', onHash)
      if (off) off()
    }
  }, [store])
}
